// The preview-then-apply import, ONCE, for both master imports (code review
// G-10, D-284): References → Vehicles'
// fleet spreadsheet and References → Vendors' Point Click Care list.
//
// The file is parsed on the SERVER. The browser posts it, the API returns the
// plan it would apply — adds, per-field updates, unchanged, absent, duplicates
// and skipped rows — and Apply commits that exact plan by its token, so preview
// and commit cannot disagree about what the file said. A token expires, and an
// expired one comes back as a 409: the master may have changed since, so the
// answer is to preview again rather than retry blindly.
//
// What differs between the two imports — the explainer, the file type and the
// preview — is the caller's. This file holds the dialog frame; each preview is
// an `ImportChangeList` (owner, 2026-10-20), and the flow is `lib/importFlow.ts`.
import type { ReactNode } from 'react'
import {
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
} from '@fluentui/react-components'
import { ArrowImportRegular } from '@fluentui/react-icons'
import { AppDialog } from './AppDialog'
import { FileDropZone } from './FileDropZone'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { useDialogFormStyles } from './dialogForm'
import { ErrorBanner } from './States'
import type { ImportFlow } from '../lib/importFlow'

const useStyles = makeStyles({
  surface: { maxWidth: '760px' },
  body: { display: 'flex', flexDirection: 'column', gap: '12px' },
})

/** The Import dialog's frame: the explainer and drop zone until a file is
    read, then the caller's preview, and Cancel / Apply N Changes. */
export function ImportDialog<P>({
  open,
  onClose,
  flow,
  intro,
  accept,
  dropLabel,
  dropHint,
  writes,
  onApply,
  children,
  nested,
}: {
  open: boolean
  onClose: () => void
  flow: ImportFlow<P>
  /** What the import does, above the drop zone. */
  intro: ReactNode
  accept: string
  dropLabel: string
  dropHint: string
  /** How many writes Apply makes. */
  writes: number
  /** Apply's click, when it must ask first (the vendor list's second press). */
  onApply?: () => void
  /** The preview, once a file has been read. */
  children: ReactNode
  /** A confirmation dialog of the caller's, rendered inside this surface. */
  nested?: ReactNode
}) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const close = () => {
    flow.reset()
    onClose()
  }
  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface className={s.surface}>
        <FrozenWhileClosed open={open}>
          <DialogBody>
            <DialogTitle>Import</DialogTitle>
            {/* The buttons 24px below the body, and the explainer 16px above
                the drop zone (`dialogForm.ts`, D-349). The preview is a report,
                not a form, and keeps its own 12px rhythm. */}
            <DialogContent className={f.beforeActions}>
              {flow.error && <ErrorBanner message={flow.error} />}
              {!flow.preview && (
                <div className={f.stack}>
                  {intro}
                  {/* The Upload screen's drop zone, not a bare file input: click
                      anywhere inside the border, or drop on it (owner item 20,
                      2026-08-28). */}
                  <FileDropZone
                    accept={accept}
                    icon={ArrowImportRegular}
                    label={dropLabel}
                    hint={dropHint}
                    disabled={flow.reading}
                    onFile={flow.onFile}
                  />
                  {flow.reading && <Spinner size="tiny" label={`Reading ${flow.fileName}…`} />}
                </div>
              )}
              {flow.preview && (
                <div className={s.body}>
                  {children}
                  {flow.applying && <Spinner size="tiny" label="Applying…" />}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={close} disabled={flow.applying}>
                Cancel
              </Button>
              {flow.preview && (
                <Button
                  appearance="primary"
                  disabled={flow.applying || writes === 0}
                  onClick={() => (onApply ? onApply() : void flow.apply())}
                >
                  {writes === 0 ? 'Nothing To Write' : `Apply ${writes} ${writes === 1 ? 'Change' : 'Changes'}`}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
        {nested}
      </DialogSurface>
    </AppDialog>
  )
}
